<!-- ExpireOverdueConfirmModal.vue -->
<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, default: false },
  onConfirm: { type: Function, required: true },
});

const emit = defineEmits(["close", "success"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. O componente não desmonta entre
// aberturas (só alterna `show`), então o watch recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

const isLoading = ref(false);
const apiError = ref(null);

function closeIfAllowed() {
  if (isLoading.value) return;
  emit("close");
}

function handleEscKey(event) {
  if (!props.show) return;
  if (event.key === "Escape" && !isLoading.value) emit("close");
}

onMounted(() => document.addEventListener("keydown", handleEscKey));
onBeforeUnmount(() => document.removeEventListener("keydown", handleEscKey));

watch(
  () => props.show,
  (val) => {
    if (val) {
      isLoading.value = false;
      apiError.value = null;
    }
  }
);

async function handleConfirm() {
  if (isLoading.value) return;

  isLoading.value = true;
  apiError.value = null;

  try {
    const result = await props.onConfirm();
    emit("success", result);
    emit("close");
  } catch (err) {
    apiError.value =
      err?.response?.data?.message ||
      err?.message ||
      "Ocorreu um erro ao processar as expirações.";
    isLoading.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="modal-backdrop" @click.self="closeIfAllowed">
      <div ref="panelRef" class="modal-panel small" :style="panelStyle">
        <!-- HEADER -->
        <div
          class="modal-header"
          :class="{
 'modal-header--draggable': isDraggable,
            'modal-header--dragging': isDragging,
          }"
          @pointerdown="onHandlePointerDown"
        >
          <h3 class="modal-title">Expirar Assinaturas Vencidas</h3>
          <button
            @click="closeIfAllowed"
            type="button"
            class="modal-close-btn"
            :disabled="isLoading"
            aria-label="Fechar"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>

        <!-- BODY -->
        <div class="modal-body">
          <div class="warning-icon">
            <svg class="w-11 h-11" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <p class="confirm-title">
            Deseja expirar todas as assinaturas vencidas?
          </p>
          <p class="confirm-subtitle">
            Esta operação varrerá todas as assinaturas ativas com data de
            término no passado, expirará cada uma e moverá os usuários
            afetados para o plano gratuito.
            <br /><br />
            Esta ação não pode ser desfeita.
          </p>

          <p v-if="apiError" class="inline-error">{{ apiError }}</p>
        </div>

        <!-- FOOTER -->
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeIfAllowed"
            :disabled="isLoading"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-warning"
            @click="handleConfirm"
            :disabled="isLoading"
          >
            <span
              v-if="isLoading"
              class="mr-2 inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
            />
            {{ isLoading ? "Processando..." : "Sim, expirar agora" }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-panel.small {
  max-width: 28rem;
}

.modal-body {
  text-align: center;
}

.warning-icon {
  display: flex;
  justify-content: center;
  color: var(--warning);
  opacity: 0.9;
  margin-top: 0.1rem;
  margin-bottom: 0.2rem;
}

.btn-warning {
  background-color: var(--warning);
  color: #ffffff;
  border-color: rgba(245, 158, 11, 0.35);
}

.btn-warning:hover:not(:disabled) {
  background-color: var(--warning);
}
</style>